import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { ArrowUpRight, Menu, X } from "lucide-react";

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Projects", to: "/projects" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = isHome && !scrolled;
  const headerBg = onDark ? "bg-transparent" : "bg-[#F9F8F6]/95 backdrop-blur border-b border-black/5";
  const linkColor = onDark ? "text-white" : "text-[#1A1D14]";
  const btnBorder = onDark ? "border-white/40" : "border-[#1A1D14]/20";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${headerBg}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" aria-label="Home">
          <Logo variant={onDark ? "light" : "dark"} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `text-[14px] tracking-wide relative py-1 transition-opacity hover:opacity-100 ${linkColor} ${isActive ? "opacity-100" : "opacity-80"}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <span className="absolute left-0 -bottom-0.5 h-[2px] w-full bg-[#B29774]" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <a href="tel:+447405681929" className={`text-[14px] ${linkColor} opacity-90 hover:opacity-100`}>
            +44 7405681929
          </a>
          <Link
            to="/contact"
            className={`inline-flex items-center gap-2 border ${btnBorder} ${linkColor} px-5 py-2.5 text-[14px] hover:bg-[#B29774] hover:text-white hover:border-[#B29774] transition-colors`}
          >
            Discuss a Project <ArrowUpRight size={16} />
          </Link>
        </div>

        <button
          className={`lg:hidden ${linkColor}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#24291E] text-white px-6 py-6">
          <div className="flex flex-col gap-5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `text-lg ${isActive ? "text-[#B29774]" : "text-white"}`}
              >
                {n.label}
              </NavLink>
            ))}
            <a href="tel:+447405681929" className="text-white/80">+44 7405681929</a>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-2 border border-white/40 px-5 py-2.5 w-fit">
              Discuss a Project <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
